import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Hexagon, Loader2, CheckCircle2, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LuxuryButton, GlassCard } from "@/components/lux";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

function SignBannerPopup() {
  const [banner, setBanner] = useState(null);
  const [closed, setClosed] = useState(false);
  useEffect(() => { api.get("/public/login-banner").then((r) => setBanner(r.data.banner)).catch(() => {}); }, []);
  if (!banner || closed) return null;
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/75 p-4" data-testid="sign-banner-popup" onClick={() => setClosed(true)}>
      <div className="relative w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => setClosed(true)} data-testid="sign-banner-close" className="absolute -right-2 -top-2 z-10 grid h-8 w-8 place-items-center rounded-full bg-black text-white shadow-lg hover:bg-black/80"><X size={16} /></button>
        <img src={banner.image} alt="Announcement" className="w-full rounded-2xl shadow-2xl" />
      </div>
    </div>
  );
}

function Shell({ title, subtitle, children }) {
  return (
    <div className="auth-green theme-light grain relative flex min-h-screen items-center justify-center overflow-hidden px-5 py-16 bg-[var(--bg)] text-[var(--text)]">
      <SignBannerPopup />
      <img src="/hero-trading.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(150deg, rgba(6,20,13,0.92) 0%, rgba(11,61,36,0.85) 45%, rgba(22,163,74,0.55) 100%)" }} />
      <div className="relative w-full max-w-md fade-up">
        <Link to="/" className="mb-8 flex items-center justify-center">
          <img src="/logo.png" alt="Alexa Fintech" className="h-16 w-auto object-contain drop-shadow-lg" />
        </Link>
        <GlassCard shine className="p-8" style={{ background: "rgba(255,255,255,0.97)", border: "1px solid rgba(22,163,74,0.35)" }}>
          <h1 className="font-display text-2xl font-extrabold text-[#16a34a]">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-[var(--text-dim)]">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </GlassCard>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-[rgba(22,163,74,0.25)] bg-white px-4 py-3 text-sm text-[#0b2016] outline-none transition placeholder:text-[var(--text-dim)] focus:border-[#16a34a] focus:ring-2 focus:ring-[rgba(22,163,74,0.18)]";

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const doLogin = async (e, ov) => {
    if (e) e.preventDefault();
    setBusy(true);
    try {
      const u = await login(ov?.email ?? email, ov?.password ?? password);
      toast.success("Welcome back");
      nav(["super_admin", "admin", "manager", "support"].includes(u.role) ? "/admin" : "/app");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <Shell title="Member Login" subtitle="Sign in to your Alexa Fintech account">
      <form onSubmit={(e) => doLogin(e)} className="space-y-4">
        <input className={inputCls} placeholder="Email or Member ID" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" required />
        <input className={inputCls} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" required />
        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-xs font-semibold text-[#16a34a] hover:underline" data-testid="forgot-link">Forgot password?</Link>
        </div>
        <LuxuryButton type="submit" className="w-full" disabled={busy} data-testid="login-submit">
          {busy ? <Loader2 className="mx-auto animate-spin" size={18} /> : "Sign In"}
        </LuxuryButton>
        <a href="https://t.me/alexafintech" target="_blank" rel="noopener noreferrer" className="btn3d btn3d-primary mt-4 flex w-full items-center justify-center gap-2 py-3 text-sm font-extrabold shadow-[0_8px_24px_rgba(37,99,235,0.35)]" data-testid="login-telegram-button">Join Official Telegram Channel</a>
      </form>

      <p className="mt-6 text-center text-sm text-[var(--text-dim)]">
        New here? <Link to="/register" className="font-semibold text-[#16a34a] hover:underline" data-testid="to-register">Create an account</Link>
      </p>
    </Shell>
  );
}

export function Register() {
  const { register, refresh } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const qp = new URLSearchParams(loc.search);
  const linkedSponsor = qp.get("sponsor") || "";
  const lockedSide = qp.get("side") === "left" || qp.get("side") === "right" ? qp.get("side") : null;
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", confirm_password: "", sponsor_id: linkedSponsor, country: "", position: lockedSide || "left", terms: false });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.sponsor_id.trim()) return toast.error("Sponsor ID is required");
    if (!f.phone.trim()) return toast.error("Contact Number is required");
    if (f.password !== f.confirm_password) return toast.error("Passwords do not match");
    if (!f.terms) return toast.error("Please accept the terms");
    setBusy(true);
    try {
      const u = await register({ ...f });
      const credentials = {
        name: f.name,
        member_id: u.member_id,
        email: f.email,
        password: f.password,
      };
      setCreated(credentials);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    const Row = ({ label, value, tid }) => (
      <div className="flex items-center justify-between gap-3 border-b border-[rgba(18,60,140,0.1)] py-2 last:border-0">
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-dim)]">{label}</span>
        <span className="font-bold text-[#16a34a]" data-testid={tid}>{value}</span>
      </div>
    );
    return (
      <Shell title="Ready to Login" subtitle="Your account has been created successfully">
        <div className="fixed inset-0 z-[100] grid place-items-center bg-black/70 p-4" data-testid="registration-credentials-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-50 p-3 text-sm text-emerald-700">
              <CheckCircle2 size={18} /> Welcome aboard! Your account is ready.
            </div>
            <h2 className="mt-5 text-lg font-extrabold text-[#0b2016]">Your Login Details</h2>
            <div className="mt-3 rounded-xl border border-[rgba(18,60,140,0.2)] bg-white px-4 py-1">
              <Row label="Name" value={created.name} tid="success-name" />
              <Row label="Member ID" value={created.member_id} tid="success-member-id" />
              <Row label="Email" value={created.email} tid="success-email" />
              <Row label="Password" value={created.password} tid="success-password" />
            </div>
            <p className="mt-3 text-xs text-[var(--text-dim)]">Save these details securely. Your welcome email has also been sent to your registered email address.</p>
            <LuxuryButton className="mt-4 w-full" onClick={() => nav("/login")} data-testid="reg-continue">Go to Login</LuxuryButton>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="Create Account" subtitle="Join the Alexa Fintech network">
      <form onSubmit={submit} className="space-y-4">
        <input className={inputCls} placeholder="Full name" value={f.name} onChange={upd("name")} data-testid="reg-name" required />
        <input className={inputCls} type="email" placeholder="Email" value={f.email} onChange={upd("email")} data-testid="reg-email" required />
        <input className={inputCls} type="tel" inputMode="tel" autoComplete="tel" placeholder="Contact Number" value={f.phone} onChange={upd("phone")} data-testid="reg-phone" required />
        <div className="grid grid-cols-2 gap-3">
          <input className={inputCls} type="password" placeholder="Password" value={f.password} onChange={upd("password")} data-testid="reg-password" required />
          <input className={inputCls} type="password" placeholder="Confirm" value={f.confirm_password} onChange={upd("confirm_password")} data-testid="reg-confirm" required />
        </div>
        <input className={`${inputCls} ${linkedSponsor ? "opacity-70" : ""}`} placeholder="Sponsor ID (required)" value={f.sponsor_id} onChange={upd("sponsor_id")} data-testid="reg-sponsor" readOnly={!!linkedSponsor} required />
        <input className={inputCls} placeholder="Country (optional)" value={f.country} onChange={upd("country")} data-testid="reg-country" />
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--text-dim)]">Placement Side</p>
          {lockedSide ? (
            <div className={`btn3d ${lockedSide === "left" ? "btn3d-primary" : "btn3d-maroon"} w-full py-2.5 text-center text-sm`} data-testid="reg-side-locked">
              Joining on {lockedSide === "left" ? "Left" : "Right"} leg (from referral link)
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setF({ ...f, position: "left" })} className={`btn3d ${f.position === "left" ? "btn3d-primary" : "btn3d-dark"} py-2.5 text-sm`} data-testid="reg-side-left">Left</button>
              <button type="button" onClick={() => setF({ ...f, position: "right" })} className={`btn3d ${f.position === "right" ? "btn3d-maroon" : "btn3d-dark"} py-2.5 text-sm`} data-testid="reg-side-right">Right</button>
            </div>
          )}
        </div>
        <label className="flex items-start gap-2.5 text-xs text-[var(--text-dim)]">
          <input type="checkbox" checked={f.terms} onChange={(e) => setF({ ...f, terms: e.target.checked })} className="mt-0.5" data-testid="reg-terms" />
          I accept the terms and understand rewards are performance-based and not guaranteed.
        </label>
        <LuxuryButton type="submit" className="w-full" disabled={busy} data-testid="reg-submit">
          {busy ? <Loader2 className="mx-auto animate-spin" size={18} /> : "Create Account"}
        </LuxuryButton>
      </form>
      <p className="mt-6 text-center text-sm text-[var(--text-dim)]">
        Already a member? <Link to="/login" className="font-semibold text-[#16a34a] hover:underline">Sign in</Link>
      </p>
    </Shell>
  );
}

export function Forgot() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { default: api } = await import("@/lib/api"); await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Shell title="Reset Password" subtitle="We'll send you a secure reset link">
      {sent ? (
        <div className="space-y-4">
          <p className="text-sm text-[var(--text-dim)]">If that email is registered, a reset link has been sent. Check your inbox.</p>
          <Link to="/login" className="text-sm font-semibold text-[#16a34a] hover:underline">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <input className={inputCls} type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" required />
          <LuxuryButton type="submit" className="w-full" disabled={busy} data-testid="forgot-submit">
            {busy ? <Loader2 className="mx-auto animate-spin" size={18} /> : "Send Reset Link"}
          </LuxuryButton>
          <Link to="/login" className="block text-center text-sm font-semibold text-[#16a34a] hover:underline">Back to sign in</Link>
        </form>
      )}
    </Shell>
  );
}

export function Reset() {
  const loc = useLocation();
  const nav = useNavigate();
  const token = new URLSearchParams(loc.search).get("token") || "";
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { default: api } = await import("@/lib/api");
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset. Please sign in."); nav("/login");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Shell title="Set New Password" subtitle="Choose a strong password">
      <form onSubmit={submit} className="space-y-4">
        <input className={inputCls} type="password" placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" required />
        <LuxuryButton type="submit" className="w-full" disabled={busy || !token} data-testid="reset-submit">
          {busy ? <Loader2 className="mx-auto animate-spin" size={18} /> : "Reset Password"}
        </LuxuryButton>
        <Link to="/login" className="block text-center text-sm font-semibold text-[#16a34a] hover:underline">Back to sign in</Link>
      </form>
    </Shell>
  );
}
